import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { UserDao } from '@app/dao/user/user.dao';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EaRiskProfileFormBuilder } from './ea-risk-profile-form.builder';
import {
  CA_VALUE_OPTIONS,
  CONCEPT_RETURN_RISK_OPTIONS,
  DATE_CREATION_OPTIONS,
  DESIRED_YIELD_OPTIONS,
  INCOME_OPTIONS,
  INVESTMENT_DECREASE_REACTION_OPTIONS,
  INVESTMENT_KNOWLEDGE_OPTIONS,
  INVESTMENT_KNOWLEDGE_QUESTIONS,
  InvestmentKnowledgeQuestion,
  InvestmentKnowledgeType,
  LEGAL_STRUCTURE_OPTIONS,
  LOSS_TOLERANCE_OPTIONS,
  MANAGEMENT_MODE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  MONEY_GAMES_OPTIONS,
  OCCUPANCY_STATUS_OPTIONS,
  PATRIMONY_VALUE_PERSON_MORAL_OPTIONS,
  PATRIMONY_VALUE_PERSON_PHYSIQUE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  RiskProfileForm,
  SAVINGS_RATE_OPTIONS,
  SelectOption,
  TRUE_FALSE_UNKNOWN_OPTIONS,
  YES_NO_OPTIONS,
  YesNo,
} from './ea-risk-profile-form.model';

@Component({
  selector: 'ea-risk-profile-form',
  templateUrl: './ea-risk-profile-form.component.html',
  styleUrls: ['./ea-risk-profile-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaRiskProfileFormComponent),
    },
  ],
  standalone: false,
})
export class EaRiskProfileFormComponent
  extends EaRiskProfileFormBuilder
  implements OnInit, OnDestroy
{
  // ========== INPUTS ==========
  @Input() override isPersonMoral: boolean = false;
  @Input() loanCount: number = 0;
  @Input() loanTotal: number = 0;
  @Input() ownerId: number;

  // ========== OUTPUTS ==========
  @Output() formSubmit = new EventEmitter<RiskProfileForm>();
  @Output() formCancel = new EventEmitter<void>();

  // ========== OPTIONS ==========
  readonly legalStructureOptions = LEGAL_STRUCTURE_OPTIONS;
  readonly caValueOptions = CA_VALUE_OPTIONS;
  readonly dateCreationOptions = DATE_CREATION_OPTIONS;
  readonly yesNoOptions = YES_NO_OPTIONS;
  readonly maritalStatusOptions = MARITAL_STATUS_OPTIONS;
  readonly occupancyStatusOptions = OCCUPANCY_STATUS_OPTIONS;
  readonly incomeOptions = INCOME_OPTIONS;
  readonly riskToleranceOptions = RISK_TOLERANCE_OPTIONS;
  readonly desiredYieldOptions = DESIRED_YIELD_OPTIONS;
  readonly conceptReturnRiskOptions = CONCEPT_RETURN_RISK_OPTIONS;
  readonly lossToleranceOptions = LOSS_TOLERANCE_OPTIONS;
  readonly savingsRateOptions = SAVINGS_RATE_OPTIONS;
  readonly investmentDecreaseReactionOptions =
    INVESTMENT_DECREASE_REACTION_OPTIONS;
  readonly moneyGamesOptions = MONEY_GAMES_OPTIONS;
  readonly investmentKnowledgeOptions = INVESTMENT_KNOWLEDGE_OPTIONS;
  readonly trueFalseUnknownOptions = TRUE_FALSE_UNKNOWN_OPTIONS;
  readonly managementModeOptions = MANAGEMENT_MODE_OPTIONS;
  readonly investmentKnowledgeQuestions = INVESTMENT_KNOWLEDGE_QUESTIONS;

  // ========== COMPUTED OPTIONS ==========
  patrimonyValueOptions: SelectOption[] = [];

  // ========== STATE ==========
  doneSubject: Subject<void> = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Risk profile chart image URL
  readonly riskChartImageUrl =
    'https://preconys-domains-prod-documents.s3.eu-west-3.amazonaws.com/profil-de-risque/chart.png';

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private cdr: ChangeDetectorRef,
  ) {
    super(formConstructor);
  }

  /**
   * Override initForm to handle form initialization from prFormService.open()
   * This is called by the form container when the form is opened
   */
  override initForm(entity: any, options?: { isPersonMoral?: boolean }): void {
    // Store entity data
    this.entity = entity;

    // Apply options
    if (options?.isPersonMoral !== undefined) {
      this.isPersonMoral = options.isPersonMoral;
    }

    // Add wide sidebar class to container
    const container = document.querySelector('.pr-form-container');
    if (container) {
      container.classList.add('pr-form-container--wide');
    }

    // Set patrimony value options based on person type
    this.updatePatrimonyValueOptions();

    // Generate the form with entity data
    this.form = this.generate(entity, { isPersonMoral: this.isPersonMoral });

    // Setup subscriptions after form is created
    this.setupFormSubscriptions();
  }

  ngOnInit(): void {
    // Form is already initialized via initForm() called by prFormService
    // ngOnInit is only used for additional setup if needed

    // If form is not yet initialized (fallback), initialize with empty data
    if (!this.form || Object.keys(this.form.controls).length === 0) {
      this.updatePatrimonyValueOptions();
      this.form = this.generate(null, { isPersonMoral: this.isPersonMoral });
      this.setupFormSubscriptions();
    }
  }

  ngOnDestroy(): void {
    // Remove wide sidebar class from container
    const container = document.querySelector('.pr-form-container');
    if (container) {
      container.classList.remove('pr-form-container--wide');
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormSubscriptions(): void {
    // Update validators initially
    this.updateValidators();

    // Subscribe to investmentKnowledge changes
    this.form
      .get('investmentKnowledge')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateValidators();
      });

    // Subscribe to invest* toggle changes to update management mode validators
    this.setupInvestToggleSubscriptions();
  }

  private updatePatrimonyValueOptions(): void {
    this.patrimonyValueOptions = this.isPersonMoral
      ? PATRIMONY_VALUE_PERSON_MORAL_OPTIONS
      : PATRIMONY_VALUE_PERSON_PHYSIQUE_OPTIONS;
  }

  private setupInvestToggleSubscriptions(): void {
    const investFields = [
      'investMonetaryProduct',
      'investBond',
      'investStock',
      'investScpi',
      'investPrivateEquity',
      'investLifeInsurance',
      'investDerivativeProducts',
    ];

    investFields.forEach(field => {
      this.form
        .get(field)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateValidators();
        });
    });
  }

  // ========== GETTERS ==========

  get showCreditInfo(): boolean {
    return !this.isPersonMoral && this.form.get('credit')?.value === YesNo.YES;
  }

  get selectedInvestmentKnowledge(): string[] {
    return this.form.get('investmentKnowledge')?.value ?? [];
  }

  // ========== HELPERS ==========

  isKnowledgeSelected(type: InvestmentKnowledgeType): boolean {
    return this.selectedInvestmentKnowledge.includes(type);
  }

  hasInvested(investField: string): boolean {
    return this.form.get(investField)?.value === true;
  }

  getActiveInvestmentQuestions(): InvestmentKnowledgeQuestion[] {
    return this.investmentKnowledgeQuestions.filter(q =>
      this.isKnowledgeSelected(q.type),
    );
  }

  // ========== FORM ACTIONS ==========

  onSubmit(): void {
    const formData = this.extract();

    if (!this.ownerId) {
      console.error('ownerId is missing!');
      this.notificationService.error('Erreur: ID utilisateur manquant');
      return;
    }

    // Sauvegarder via l'API
    this.prFormService.loading();
    this.userDao
      .createOrUpdateOwnerRiskProfile(this.ownerId, formData)
      .subscribe({
        next: response => {
          this.prFormService.loaded();
          this.notificationService.success(
            'Le profil de risque a été sauvegardé',
          );
          this.formSubmit.emit(formData);
          this.doneSubject.next();
        },
        error: error => {
          console.error(
            'Erreur lors de la sauvegarde du profil de risque:',
            error,
          );
          this.prFormService.loaded();
          this.notificationService.error('La sauvegarde a échoué');
        },
      });
  }

  onCancel(): void {
    this.formCancel.emit();
  }

  /**
   * Updates isPersonMoral and refreshes form validators.
   * Call this if the person type changes after initialization.
   */
  setPersonType(isPersonMoral: boolean): void {
    this.isPersonMoral = isPersonMoral;
    this.updatePatrimonyValueOptions();
    this.updateValidators();

    // Reset patrimony value when switching type as options are different
    this.form.get('patrimonyValue')?.setValue(null);
  }
}
